import { useEffect, useState } from "react";
import { adminPanchangLabInfo, adminPanchangLabRun } from "@/lib/api";
import { FlaskConical, Play, Server, CheckCircle2, XCircle, Clock, Copy } from "lucide-react";
import { toast } from "sonner";

// Admin Panchangam Test Engine — send ANY path + JSON payload to the external
// PythonAnywhere Jyotish engine and inspect the RAW response. Lets the owner
// verify new engine capabilities/data points directly in the CMS. Read-only
// against the engine; no calculations happen locally (THE ONE RULE).
export default function AdminPanchangLab() {
  const [info, setInfo] = useState(null);
  const [path, setPath] = useState("panchang");
  const [body, setBody] = useState("{}");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [jsonErr, setJsonErr] = useState("");

  useEffect(() => {
    adminPanchangLabInfo()
      .then((d) => {
        setInfo(d);
        const first = d.endpoints?.[0];
        if (first) { setPath(first.path); setBody(JSON.stringify(first.sample, null, 2)); }
      })
      .catch(() => setInfo({ configured: false, endpoints: [] }));
  }, []);

  const pickEndpoint = (ep) => {
    setPath(ep.path);
    setBody(JSON.stringify(ep.sample, null, 2));
    setResult(null);
    setJsonErr("");
  };

  const run = async () => {
    let parsed;
    try { parsed = JSON.parse(body); }
    catch (e) { setJsonErr("Invalid JSON: " + e.message); return; }
    setJsonErr("");
    setRunning(true);
    setResult(null);
    try {
      const r = await adminPanchangLabRun(path.trim(), parsed);
      setResult(r);
      if (!r.ok) toast.error(r.error || "Engine error");
    } catch (e) {
      setResult({ ok: false, error: e?.response?.data?.detail || e.message, status: e?.response?.status || 0 });
    } finally {
      setRunning(false);
    }
  };

  const copyResp = () => {
    try { navigator.clipboard.writeText(JSON.stringify(result?.response ?? result, null, 2)); toast.success("Copied"); } catch {}
  };

  return (
    <div className="max-w-5xl" data-testid="admin-panchang-lab">
      <div className="flex items-center gap-2">
        <FlaskConical className="h-5 w-5 text-saffron" />
        <h1 className="font-serif-latin text-2xl text-ink">Panchangam Test Engine</h1>
      </div>
      <p className="mt-1 text-sm text-ink-3">
        Send any endpoint + JSON payload to the external Jyotish engine and inspect the raw output.
        Use this to verify new calculations or data points you add on the engine side — no values are computed here.
      </p>

      {/* Engine status */}
      <div className="mt-4 flex flex-wrap items-center gap-3 rounded-lg border border-line bg-surface p-3 text-sm">
        <Server className="h-4 w-4 text-ink-3" />
        {info ? (
          info.configured ? (
            <>
              <span className="inline-flex items-center gap-1 text-emerald-600"><CheckCircle2 className="h-4 w-4" /> Connected</span>
              <span className="truncate text-ink-3" data-testid="lab-base-url">{info.base_url}</span>
              <span className="text-ink-3">· Key: {info.has_key ? "set" : "none"}</span>
            </>
          ) : (
            <span className="inline-flex items-center gap-1 text-rose-600"><XCircle className="h-4 w-4" /> Engine not configured (PANCHANG_API_URL missing)</span>
          )
        ) : (
          <span className="text-ink-3">Loading…</span>
        )}
      </div>

      {/* Endpoint presets */}
      <div className="mt-5 flex flex-wrap gap-2" data-testid="lab-endpoints">
        {(info?.endpoints || []).map((ep) => (
          <button key={ep.path} data-testid={`lab-endpoint-${ep.path.replace(/\//g, "-")}`} onClick={() => pickEndpoint(ep)}
            className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${path === ep.path ? "border-saffron bg-[rgba(199,80,0,0.08)] text-saffron" : "border-line text-ink-2 hover:border-ink-3"}`}>
            {ep.label}
          </button>
        ))}
      </div>

      <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-2">
        {/* Request */}
        <div>
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-ink-3">Endpoint path</label>
          <input data-testid="lab-path-input" value={path} onChange={(e) => setPath(e.target.value)}
            placeholder="e.g. panchang, lab/chart, or a new custom path"
            className="w-full rounded-md border border-line bg-surface px-3 py-2 font-mono text-sm text-ink outline-none focus:border-saffron" />

          <label className="mb-1.5 mt-4 block text-xs font-semibold uppercase tracking-wide text-ink-3">JSON payload</label>
          <textarea data-testid="lab-payload-input" value={body} onChange={(e) => setBody(e.target.value)} rows={14} spellCheck={false}
            className="w-full resize-y rounded-md border border-line bg-surface px-3 py-2 font-mono text-[13px] leading-relaxed text-ink outline-none focus:border-saffron" />
          {jsonErr && <p className="mt-1.5 text-xs text-rose-600" data-testid="lab-json-error">{jsonErr}</p>}

          <button data-testid="lab-run-button" onClick={run} disabled={running || !info?.configured}
            className="mt-4 inline-flex items-center gap-2 rounded-md bg-saffron px-5 py-2.5 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50">
            <Play className="h-4 w-4" /> {running ? "Running…" : "Run"}
          </button>
        </div>

        {/* Response */}
        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <label className="block text-xs font-semibold uppercase tracking-wide text-ink-3">Engine response</label>
            {result && (
              <div className="flex items-center gap-3 text-xs">
                <span className={`inline-flex items-center gap-1 ${result.ok ? "text-emerald-600" : "text-rose-600"}`} data-testid="lab-status">
                  {result.ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
                  HTTP {result.status}
                </span>
                {result.elapsed_ms != null && <span className="inline-flex items-center gap-1 text-ink-3"><Clock className="h-3.5 w-3.5" />{result.elapsed_ms}ms</span>}
                <button onClick={copyResp} className="inline-flex items-center gap-1 text-ink-3 hover:text-saffron"><Copy className="h-3.5 w-3.5" /> Copy</button>
              </div>
            )}
          </div>
          <pre data-testid="lab-result" className="h-[26rem] overflow-auto rounded-md border border-line bg-[var(--surface-alt)] p-3 font-mono text-[12px] leading-relaxed text-ink">
{result
  ? (result.error ? `// ${result.error}\n\n` : "") + JSON.stringify(result.response ?? result.raw_text ?? result, null, 2)
  : "// Pick an endpoint, edit the payload, and Run."}
          </pre>
        </div>
      </div>
    </div>
  );
}
